<template>
  <div class="sidebar-nav">
    <transition name="sidebar-drill" mode="out-in">
      <!-- Main -->
      <ul
        v-if="!drillParent"
        key="main"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Dashboard')"
            to="/dashboard"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <i class="fa fa-home app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Dashboard</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Calendar')"
            to="/calendar"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <i class="fa fa-calendar app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Calendar</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('clients', 'view')"
        >
          <router-link
            v-tooltip.right="sidebarTip($filters.customerLabel('plural', true))"
            :to="{ name: 'patients' }"
            class="app-sidebar__link app-sidebar__link--pill"
            :class="{
              'router-link-active': $route.path.startsWith('/clients'),
            }"
            @click="emitNavigated"
          >
            <i class="fa fa-users app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">{{
              $filters.customerLabel("plural", true)
            }}</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('providers', 'view')"
        >
          <router-link
            v-tooltip.right="sidebarTip($filters.providerLabel('plural', true))"
            :to="{ name: 'providers' }"
            class="app-sidebar__link app-sidebar__link--pill"
            :class="{
              'router-link-active': $route.path.startsWith('/providers'),
            }"
            @click="emitNavigated"
          >
            <i class="fa fa-user-md app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">{{
              $filters.providerLabel("plural", true)
            }}</span>
          </router-link>
        </li>
        <UnreadAlert :collapsed="collapsed" @navigated="emitNavigated" />
        <li class="app-sidebar__item">
          <router-link
            v-tooltip.right="sidebarTip('Meeting Rooms')"
            :to="{ name: 'rooms' }"
            class="app-sidebar__link app-sidebar__link--pill text-nowrap"
            :class="{ 'router-link-active': $route.path.startsWith('/rooms') }"
            @click="emitNavigated"
          >
            <i class="fa fa-door-open app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Meeting Rooms</span>
          </router-link>
        </li>
        <PendingBookingRequests
          :collapsed="collapsed"
          @navigated="emitNavigated"
        />
        <li
          class="app-sidebar__item"
          v-if="!publicSettingsLoading && publicSettings.STRIPE_ENABLED"
        >
          <button
            v-tooltip.right="sidebarTip('Payments')"
            type="button"
            class="
              app-sidebar__link
              app-sidebar__link--pill
              app-sidebar__link--parent
              w-100
              text-left
            "
            :class="{
              'app-sidebar__link--section-active':
                activeDrillSectionFromRoute === 'payments',
            }"
            @click="openDrill('payments')"
          >
            <i class="fa fa-credit-card app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Payments</span>
            <i
              class="fa fa-chevron-right app-sidebar__chevron"
              aria-hidden="true"
            />
          </button>
        </li>
        <li class="app-sidebar__item" v-if="showReportsMenu">
          <button
            v-tooltip.right="sidebarTip('Reports')"
            type="button"
            class="
              app-sidebar__link
              app-sidebar__link--pill
              app-sidebar__link--parent
              w-100
              text-left
            "
            :class="{
              'app-sidebar__link--section-active':
                activeDrillSectionFromRoute === 'reports',
            }"
            @click="openDrill('reports')"
          >
            <i class="fa fa-chart-bar app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Reports</span>
            <i
              class="fa fa-chevron-right app-sidebar__chevron"
              aria-hidden="true"
            />
          </button>
        </li>
        <li class="app-sidebar__item">
          <button
            v-tooltip.right="sidebarTip('Admin')"
            type="button"
            class="
              app-sidebar__link
              app-sidebar__link--pill
              app-sidebar__link--parent
              w-100
              text-left
            "
            :class="{
              'app-sidebar__link--section-active':
                activeDrillSectionFromRoute === 'admin',
            }"
            @click="openDrill('admin')"
          >
            <i class="fa fa-cog app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">Admin</span>
            <i
              class="fa fa-chevron-right app-sidebar__chevron"
              aria-hidden="true"
            />
          </button>
        </li>
        <li class="app-sidebar__item">
          <button
            v-tooltip.right="sidebarTip('My Profile')"
            type="button"
            class="
              app-sidebar__link
              app-sidebar__link--pill
              app-sidebar__link--parent
              w-100
              text-left
            "
            :class="{
              'app-sidebar__link--section-active':
                activeDrillSectionFromRoute === 'profile',
            }"
            @click="openDrill('profile')"
          >
            <i class="fa fa-user app-sidebar__icon" aria-hidden="true" />
            <span class="app-sidebar__label">My Profile</span>
            <i
              class="fa fa-chevron-right app-sidebar__chevron"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>

      <!-- Payments -->
      <ul
        v-else-if="drillParent === 'payments'"
        key="payments"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li class="app-sidebar__item">
          <router-link
            to="/creditCards"
            v-if="!publicSettingsLoading && publicSettings.STRIPE_ENABLED"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Credit Cards</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            v-if="!publicSettingsLoading && publicSettings.STRIPE_ENABLED"
            to="/transactions"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Transactions</span>
          </router-link>
        </li>
      </ul>

      <!-- Reports -->
      <ul
        v-else-if="drillParent === 'reports'"
        key="reports"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('payrollReport', 'view')"
        >
          <router-link
            to="/admin/reports/payroll"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Payroll Report</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('billingReport', 'view')"
        >
          <router-link
            to="/admin/reports/billing"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Billing Report</span>
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="$isAdmin()">
          <router-link
            to="/admin/reports/group-sessions"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Group Sessions</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('maxHoursReport', 'view')"
        >
          <router-link
            to="/admin/reports/maxhours"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Budget Report</span>
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="publicSettings.HCAI_ENABLED">
          <router-link
            to="/admin/reports/hcai-details"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">HCAI Reports</span>
          </router-link>
        </li>
      </ul>

      <!-- Admin tools -->
      <ul
        v-else-if="drillParent === 'admin'"
        key="admin"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li
          class="app-sidebar__item"
          v-if="
            user &&
            !user.isManagingAdmin &&
            $isAdmin() &&
            $can('adminUser', 'view')
          "
        >
          <router-link
            to="/admin/users"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Admin Users</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/users-availability"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Users Availability</span>
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="$isSuperAdmin()">
          <router-link
            to="/admin/users-activity"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">User Activity</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('clinics', 'view')"
        >
          <router-link
            to="/admin/clinics"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">{{
              $filters.clinicLabel("plural", true)
            }}</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('pharmacies', 'view')"
        >
          <router-link
            to="/admin/pharmacies"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Pharmacies</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('consents', 'view')"
        >
          <router-link
            to="/admin/consents"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Consents</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('classes', 'view')"
        >
          <router-link
            to="/admin/classes"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Classes</span>
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="user && !user.isManagingAdmin">
          <router-link
            to="/admin/settings"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Settings</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('services', 'view')"
        >
          <router-link
            to="/admin/services"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Services</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('services', 'view')"
        >
          <router-link
            to="/admin/treatments"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Treatments</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="
            !publicSettingsLoading &&
            publicSettings.GROUP_SESSIONS_ENABLED &&
            $isAdmin() &&
            $can('programs', 'view')
          "
        >
          <router-link
            to="/admin/programs"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">{{
              $filters.programLabel("plural", true)
            }}</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="!publicSettingsLoading && publicSettings.GROUP_SESSIONS_ENABLED"
        >
          <router-link
            to="/group-sessions"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Group Sessions</span>
          </router-link>
        </li>
        <li
          class="app-sidebar__item"
          v-if="$isAdmin() && $can('professions', 'view')"
        >
          <router-link
            to="/admin/professions"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Professions</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/referral-sources"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Referral Sources</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/locations"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Locations</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/documents"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Documents</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/intake-categories"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Intake Categories</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/task-types"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Task Types</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/clients-import"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label"
              >Import {{ $filters.customerLabel("plural", true) }}</span
            >
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/admin/providers-import"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label"
              >Import {{ $filters.providerLabel("plural", true) }}</span
            >
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="publicSettings.HCAI_ENABLED">
          <router-link
            to="/admin/hcai-documents"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">HCAI Details</span>
          </router-link>
        </li>
        <li class="app-sidebar__item">
          <router-link
            to="/templates"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label">Form Templates</span>
          </router-link>
        </li>
        <li class="app-sidebar__item" v-if="$isSuperAdmin()">
          <router-link
            to="/clients-contacts"
            class="app-sidebar__link app-sidebar__link--pill"
            @click="emitNavigated"
          >
            <span class="app-sidebar__label"
              >{{ $filters.customerLabel("plural", true) }} Contacts</span
            >
          </router-link>
        </li>
      </ul>

      <!-- My Profile -->
      <ul
        v-else-if="drillParent === 'profile'"
        key="profile"
        class="app-sidebar__list list-unstyled mb-0"
      >
        <li class="app-sidebar__item">
          <a
            href="/"
            class="app-sidebar__link app-sidebar__link--pill"
            @click.prevent="profileNavigate"
          >
            <span class="app-sidebar__label">Profile Update</span>
          </a>
        </li>
        <li class="app-sidebar__item">
          <a
            href="#"
            class="app-sidebar__link app-sidebar__link--pill"
            @click.prevent="handleLogout"
          >
            <span class="app-sidebar__label">Logout</span>
          </a>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import PendingBookingRequests from "@/components/PendingBookingRequests.vue";
import UnreadAlert from "@/components/UnreadAlert.vue";

const DRILL_LABELS = {
  payments: "Payments",
  reports: "Reports",
  admin: "Admin",
  profile: "My Profile",
};

export default {
  name: "SidebarNavAdmin",
  components: {
    UnreadAlert,
    PendingBookingRequests,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["drill-change", "navigated"],
  data() {
    return {
      drillParent: null,
      suppressRouteSync: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      publicSettings: (state) => state.settings.publicSettings?.data ?? {},
      publicSettingsLoading: (state) =>
        state.settings.publicSettings?.loading ?? true,
      companyName: (state) => (state.settings.companyProfile || {}).name || "",
      deviceType: (state) => state.settings.deviceType,
    }),
    isMobileTablet: function () {
      return this.deviceType == "mobile" || this.deviceType == "tablet";
    },
    showReportsMenu() {
      return (
        (this.$isAdmin() && this.$can("payrollReport", "view")) ||
        (this.$isAdmin() && this.$can("billingReport", "view")) ||
        (this.$isAdmin() && this.$can("maxHoursReport", "view"))
      );
    },
    /** When main list is shown (e.g. after Back), still highlight parent by route. */
    activeDrillSectionFromRoute() {
      return this.computeAdminDrillFromPath(this.$route.path);
    },
  },
  watch: {
    $route: {
      handler() {
        this.suppressRouteSync = false;
        this.applyRouteToDrill();
      },
      immediate: true,
    },
  },
  mounted() {
    this.getPublicSettings();
  },
  beforeUnmount() {
    if (window.Echo) {
      window.Echo.leaveChannel("onechart-notes-info");
    }
  },
  methods: {
    ...mapActions({
      getPublicSettings: "settings/getPublicSettings",
      doLogout: "auth/logout",
    }),
    sidebarTip(text) {
      if (!this.collapsed || this.isMobileTablet) return false;
      return text;
    },
    computeAdminDrillFromPath(path) {
      if (!path) return null;
      if (path.startsWith("/creditCards") || path.startsWith("/transactions")) {
        return "payments";
      }
      if (path.startsWith("/admin/reports")) return "reports";
      if (path.startsWith("/admin/profile")) return "profile";
      if (
        path.startsWith("/templates") ||
        path.startsWith("/clients-contacts") ||
        path.startsWith("/group-sessions") ||
        (path.startsWith("/admin/") &&
          !path.startsWith("/admin/reports") &&
          !path.startsWith("/admin/profile"))
      ) {
        return "admin";
      }
      return null;
    },
    applyRouteToDrill() {
      if (this.suppressRouteSync) return;
      // If sidebar is collapsed (and not on mobile/tablet), do not activate drill
      if (this.collapsed && !this.isMobileTablet) {
        this.drillParent = null;
        this.emitDrillState(false);
        return;
      }
      const next = this.computeAdminDrillFromPath(this.$route.path);
      this.drillParent = next;
      this.emitDrillState(false);
    },
    openDrill(key) {
      this.suppressRouteSync = false;
      this.drillParent = key;
      this.emitDrillState(true);
    },
    goBackDrill() {
      this.suppressRouteSync = true;
      this.drillParent = null;
      this.emitDrillState(false);
    },
    emitDrillState(userInitiated = false) {
      if (!this.drillParent) {
        this.$emit("drill-change", {
          showBack: false,
          title: "",
          userInitiated: !!userInitiated,
        });
        return;
      }
      const base = DRILL_LABELS[this.drillParent] || "";
      this.$emit("drill-change", {
        showBack: true,
        title: base ? `${base} Options` : "",
        userInitiated: !!userInitiated,
      });
    },
    emitNavigated() {
      this.$emit("navigated");
    },
    profileNavigate() {
      this.$router.push({ path: "/admin/profile" });
      this.emitNavigated();
    },
    handleLogout() {
      this.doLogout().then((res) => {
        if (res) {
          this.$router.push({ path: "/login" });
          window.location.href = `${window.location.origin}`;
        }
      });
    },
  },
};
</script>
